import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GeoJSON, ImageOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import proj4 from 'proj4';
import LayerControl from './LayerControl';
import dlpLayers from '../../data/dlpLayers.json';

const BASE_MAPS = {
  osm: {
    id: 'osm',
    label: 'Street',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
    subdomains: 'abc',
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
    crossOrigin: true,
    subdomains: undefined,
  },
};

const BaseTileLayer = ({ baseKey }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const config = BASE_MAPS[baseKey] || BASE_MAPS.osm;
    const layerOptions = {
      attribution: config.attribution,
      maxZoom: config.maxZoom,
      crossOrigin: config.crossOrigin ? 'anonymous' : undefined,
    };
    const subdomains =
      config.subdomains !== undefined
        ? config.subdomains
        : config.url.includes('{s}')
          ? 'abc'
          : undefined;
    if (subdomains !== undefined) {
      layerOptions.subdomains = subdomains;
    }
    const layer = L.tileLayer(config.url, layerOptions);
    layer.on('tileerror', (event) => {
      console.warn(`Tile error for ${config.id}`, event?.error || event);
    });
    layer.addTo(map);
    return () => {
      layer.off('tileerror');
      map.removeLayer(layer);
    };
  }, [map, baseKey]);

  return null;
};

const baseLayerConfigs = {
  fisheries: {
    name: 'Fisheries Restrictions',
    geojsonUrl: '/data/Fishing_Prohibition_zones.geojson',
    style: { color: 'blue', weight: 2 },
    initialVisibility: false,
  },
  desalination_protection: {
    name: 'Desalination Water Protection Zones',
    geojsonUrl: '/data/Desalination_water_protection_zone.geojson',
    style: { color: 'green', weight: 2 },
    initialVisibility: false,
  },
  aquaculture_zones: {
    name: 'Proposed Aquaculture Zones',
    geojsonUrl: '/data/aquaculture_proposed_zones.geojson',
    style: { color: 'purple', weight: 2 },
    initialVisibility: false,
  },
  marine_antiquities: {
    name: 'Marine Antiquities Protection Zones',
    geojsonUrl: '/data/Marine_antiquities_protection_zones.geojson',
    style: { color: 'orange', weight: 2 },
    initialVisibility: false,
  },
  ports: {
    name: 'Ports',
    geojsonUrl: '/data/Ports.geojson',
    style: { color: 'yellow', weight: 2 },
    initialVisibility: false,
  },
  research: {
    name: 'Scientific Research & Testing Zones',
    geojsonUrl: '/data/Scientific_Research_Testing_Zone.geojson',
    style: { color: 'black', weight: 2 },
    initialVisibility: false,
  },
  routes: {
    name: 'Shipping Routes',
    geojsonUrl: '/data/Shipping_Routes.geojson',
    style: { color: 'yellow', weight: 2 },
    initialVisibility: false,
  },
  proposed_natura: {
    name: 'Proposed Natura 2000 Zones',
    geojsonUrl: '/data/Proposed_natura_2000_zones.geojson',
    style: { color: '#2b8cbe', weight: 2, fillOpacity: 0.4 },
    initialVisibility: false,
  },
  wind_speed: {
    name: 'Wind Speed',
    geojsonUrl: '/data/WindSpeed.geojson',
    style: { color: '#ff9800', weight: 1, fillOpacity: 0.3 },
    initialVisibility: false,
  },
  natura: {
    name: 'Natura 2000 Zones',
    geojsonUrl: '/data/Natura_2000_zones.geojson',
    style: { color: '#e6398dff', weight: 2, fillOpacity: 0.4 },
    initialVisibility: false,
  },
  isobaths: {
    name: 'Isobaths',
    geojsonUrl: '/data/Isobaths.geojson',
    style: { color: '#08519c', weight: 2, fillOpacity: 0.4 },
    initialVisibility: false,
  },
  bathymetry: {
    name: 'Bathymetry',
    geojsonUrl: '/data/Bathymetry.geojson',
    style: { color: '#0d47a1', weight: 1, fillOpacity: 0.15 },
    initialVisibility: false,
  },
  habitat: {
    name: 'Posidonia Coverage (>=1%)',
    geojsonUrl: '/data/Habitat_map_posidonia.geojson',
    style: { color: '#0f9d58', weight: 1, fillOpacity: 0.35 },
    initialVisibility: false,
  },
  cable: {
    name: 'Cable Landing Zone',
    geojsonUrl: '/data/Cable_Landing_Zone.geojson',
    style: { color: '#9c5e08ff', weight: 2, fillOpacity: 0.4 },
    initialVisibility: false,
  },
  dlp_cyprus_go: {
    name: 'DLP Cyprus Boundaries',
    geojsonUrl: '/data/DLP_Cyprus_go.geojson',
    style: { color: '#1e88e5', weight: 2, fillOpacity: 0.05 },
    initialVisibility: false,
  },

};

const defaultPolygonStyle = { color: '#1976d2', weight: 1, fillOpacity: 0.2 };

const chlorophyllLayerDefinitions = [
  {
    id: 'chl_mean_jja',
    name: 'Chlorophyll – Summer Mean (JJA)',
    type: 'raster',
    imageUrl: '/data/chl_rasters/Chl_Mean_JJA.png',
    metadataUrl: '/data/chl_rasters/Chl_Mean_JJA.json',
    defaultOpacity: 0.85,
  },
  {
    id: 'chl_max_jja',
    name: 'Chlorophyll – Summer Max (JJA)',
    type: 'raster',
    imageUrl: '/data/chl_rasters/Chl_Max_JJA.png',
    metadataUrl: '/data/chl_rasters/Chl_Max_JJA.json',
    defaultOpacity: 0.85,
  },
];

const buildCategoryStyleFn = (descriptor) => {
  if (!descriptor || !descriptor.property) {
    return () => descriptor?.defaultStyle || defaultPolygonStyle;
  }
  const categories = descriptor.categories || {};
  const fallback = descriptor.defaultStyle || defaultPolygonStyle;
  const propertyName = descriptor.property;
  return (feature) => {
    const value = feature?.properties ? feature.properties[propertyName] : undefined;
    return categories[value] || fallback;
  };
};

const dlpLayerConfigs = dlpLayers.reduce((acc, layer) => {
  const hasCategories = !!layer.styleCategories;
  acc[layer.id] = {
    name: layer.name,
    geojsonUrl: `/data/${layer.filename}`,
    style: hasCategories ? buildCategoryStyleFn(layer.styleCategories) : (layer.style || defaultPolygonStyle),
    pointStyle: layer.pointStyle || null,
    initialVisibility: false,
  };
  return acc;
}, {});

const chlorophyllLayerConfigs = chlorophyllLayerDefinitions.reduce((acc, layer) => {
  acc[layer.id] = {
    name: layer.name,
    type: layer.type || 'raster',
    imageUrl: layer.imageUrl,
    metadataUrl: layer.metadataUrl,
    defaultOpacity: layer.defaultOpacity ?? 0.85,
    initialVisibility: false,
  };
  return acc;
}, {});

const layerConfigs = { ...baseLayerConfigs, ...chlorophyllLayerConfigs, ...dlpLayerConfigs };

const defaultPointStyle = {
  radius: 6,
  fillColor: '#ff7800',
  color: '#000',
  weight: 1,
  fillOpacity: 0.9,
};

const buildPointRenderer = (customStyle) => (feature, latlng) => {
  const mergedStyle = { ...defaultPointStyle, ...(customStyle || {}) };
  return L.circleMarker(latlng, mergedStyle);
};

const popupRenderers = {
  dlp_habitat_mapping: (feature) => {
    const classes = feature?.properties?.DCLASS_all;
    if (Array.isArray(classes) && classes.length) {
      const classList = classes.join(', ');
      const countLabel = classes.length > 1 ? ` (${classes.length})` : '';
      return `<strong>Habitat types${countLabel}:</strong><br/>${classList}`;
    }
    return null;
  },
  dlp_substrate_1m: (feature) => {
    const props = feature?.properties || {};
    const lines = [];
    const folk5 = props.folk_5cl_t || 'Unknown';
    lines.push(`<strong>FOLK 5-class:</strong> ${folk5}`);
    if (props.folk_7cl_t) lines.push(`<strong>FOLK 7-class:</strong> ${props.folk_7cl_t}`);
    if (props.folk_16cl_) lines.push(`<strong>FOLK 16-class:</strong> ${props.folk_16cl_}`);
    if (props.conf_tot || props.conf_tot === 0) lines.push(`<strong>Total confidence:</strong> ${props.conf_tot}`);

    return lines.length ? lines.join('<br/>') : null;
  },
};

const defaultPopupRenderer = (feature) => {
  if (!feature || !feature.properties) return null;
  const entries = Object.entries(feature.properties).slice(0, 6);
  if (!entries.length) return null;
  return entries.map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>');
};

const buildPopupHtml = (layerId, feature) => {
  const renderer = popupRenderers[layerId];
  if (renderer) {
    const custom = renderer(feature);
    if (custom) return custom;
  }
  return defaultPopupRenderer(feature);
};

// helpers to detect/reproject
const isCRSEPSG3857 = (geojson) => {
  try {
    const name = geojson && geojson.crs && geojson.crs.properties && geojson.crs.properties.name;
    if (!name) return false;
    return /3857/.test(name) || /900913/.test(name);
  } catch (e) {
    return false;
  }
};

const looksLike3857ByCoords = (geojson) => {
  // sample a coordinate, if absolute values >> 180 it's likely meters (3857)
  const sample = (() => {
    if (!geojson) return null;
    if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features) && geojson.features.length) {
      const geom = geojson.features[0].geometry;
      const sampleCoords = (coords) => {
        if (!coords) return null;
        if (typeof coords[0] === 'number') return coords;
        return sampleCoords(coords[0]);
      };
      return sampleCoords(geom && geom.coordinates);
    }
    if (geojson.type === 'Feature' && geojson.geometry) {
      const sampleCoords = (coords) => {
        if (!coords) return null;
        if (typeof coords[0] === 'number') return coords;
        return sampleCoords(coords[0]);
      };
      return sampleCoords(geojson.geometry.coordinates);
    }
    return null;
  })();
  if (!sample || sample.length < 2) return false;
  const [a, b] = sample;
  return Math.abs(a) > 1000 || Math.abs(b) > 1000; // meters magnitude
};

// recursively transform coordinates array
const reprojectCoords = (coords, fromProj, toProj) => {
  if (coords === null || coords === undefined) return coords;
  if (typeof coords[0] === 'number') {
    // single coord [x,y,(z?)]
    const [x, y, ...rest] = coords;
    const [lon, lat] = proj4(fromProj, toProj, [x, y]);
    return rest.length ? [lon, lat, ...rest] : [lon, lat];
  }
  return coords.map(c => reprojectCoords(c, fromProj, toProj));
};

const reprojectGeoJSON = (geojson, fromProj = 'EPSG:3857', toProj = 'EPSG:4326') => {
  if (!geojson) return geojson;
  const clone = JSON.parse(JSON.stringify(geojson));
  const walkFeature = (feature) => {
    if (!feature || !feature.geometry) return feature;
    feature.geometry.coordinates = reprojectCoords(feature.geometry.coordinates, fromProj, toProj);
    return feature;
  };
  if (clone.type === 'FeatureCollection' && Array.isArray(clone.features)) {
    clone.features = clone.features.map(f => walkFeature(f));
  } else if (clone.type === 'Feature') {
    walkFeature(clone);
  } else if (clone.type === 'GeometryCollection' && Array.isArray(clone.geometries)) {
    clone.geometries = clone.geometries.map(g => {
      g.coordinates = reprojectCoords(g.coordinates, fromProj, toProj);
      return g;
    });
  } else if (clone.type && clone.coordinates) {
    // raw geometry object
    clone.coordinates = reprojectCoords(clone.coordinates, fromProj, toProj);
  }
  // remove crs property to avoid confusion
  if (clone.crs) delete clone.crs;
  return clone;
};

const buildDataBaseCandidates = () => {
  const candidates = [];
  const appendCandidate = (value) => {
    if (value === undefined || value === null) return;
    const trimmed = value.toString().trim();
    if (!trimmed) return;
    const normalized = trimmed.replace(/\/$/, '');
    if (!candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  const explicitBase = process.env.REACT_APP_DATA_BASE_URL;
  appendCandidate(explicitBase);

  const backendHost = process.env.REACT_APP_BACKEND_HOST;
  if (backendHost) {
    const hasProtocol = /^https?:\/\//i.test(backendHost);
    const portSegment = process.env.REACT_APP_BACKEND_PORT ? `:${process.env.REACT_APP_BACKEND_PORT}` : '';
    if (hasProtocol) {
      appendCandidate(backendHost);
    } else {
      const protocolHint = typeof window !== 'undefined' ? window.location.protocol : 'http:';
      const normalizedProtocol = protocolHint.endsWith(':') ? protocolHint.slice(0, -1) : protocolHint;
      appendCandidate(`${normalizedProtocol}://${backendHost}${portSegment}`);
      if (normalizedProtocol !== 'http') {
        appendCandidate(`http://${backendHost}${portSegment}`);
      }
    }
  }

  if (typeof window !== 'undefined') {
    appendCandidate(window.location.origin);
  }

  appendCandidate('');

  return candidates;
};

const dataBaseCandidates = buildDataBaseCandidates();

const LayerManager = () => {
  const map = useMap();

  const initialVisibilityState = Object.entries(layerConfigs).reduce((acc, [key, config]) => {
    if (config.initialVisibility !== undefined) acc[key] = config.initialVisibility;
    return acc;
  }, {});
  const [layerVisibility, setLayerVisibility] = useState(initialVisibilityState);
  const [geojsonData, setGeojsonData] = useState({});
  const [baseLayer, setBaseLayer] = useState('osm');

  useEffect(() => {
    const entries = Object.entries(layerConfigs).filter(([_, cfg]) => {
      if (cfg.type === 'raster') {
        return Boolean(cfg.metadataUrl);
      }
      return Boolean(cfg.geojsonUrl);
    });
    if (!entries.length) return;

    let cancelled = false;

    const fetchLayerData = async (key, cfg) => {
      let loadedLayer = null;
      let lastError = null;
      const targetPath = cfg.type === 'raster' ? cfg.metadataUrl : cfg.geojsonUrl;
      for (const base of dataBaseCandidates) {
        const normalizedBase = base || '';
        const url = `${normalizedBase}${targetPath}?_=${Date.now()}`;
        try {
          const res = await fetch(url, { cache: 'no-store' });
          if (!res.ok) {
            lastError = `HTTP ${res.status}`;
            continue;
          }
          const payload = await res.json();
          if (cfg.type === 'raster') {
            loadedLayer = payload;
          } else {
            const needsReproj = isCRSEPSG3857(payload) || looksLike3857ByCoords(payload);
            loadedLayer = needsReproj ? reprojectGeoJSON(payload, 'EPSG:3857', 'EPSG:4326') : payload;
            console.debug(
              `Loaded layer ${key} from ${url}, features: ${
                Array.isArray(loadedLayer?.features) ? loadedLayer.features.length : 'n/a'
              }, reprojected:${!!needsReproj}`
            );
          }
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!loadedLayer && lastError) {
        console.error(`Failed to load layer ${key}`, lastError);
      }

      if (!cancelled) {
        setGeojsonData(prev => ({ ...prev, [key]: loadedLayer }));
      }
    };

    entries.forEach(([key, cfg]) => {
      fetchLayerData(key, cfg);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    Object.entries(layerVisibility).forEach(([key, visible]) => {
      if (!visible) return;
      const config = layerConfigs[key];
      const data = geojsonData[key];
      if (!config || !data) return;
      try {
        if (config.type === 'raster' && Array.isArray(data.bounds)) {
          const bounds = L.latLngBounds(
            [data.bounds[1], data.bounds[0]],
            [data.bounds[3], data.bounds[2]]
          );
          if (bounds.isValid()) {
            map.fitBounds(bounds, { maxZoom: 12 });
          }
          return;
        }
        const tmp = L.geoJSON(data);
        const bounds = tmp.getBounds();
        if (bounds && !bounds.isEmpty()) map.fitBounds(bounds, { maxZoom: 14 });
      } catch (e) {
        console.debug('fitBounds failed', e);
      }
    });
  }, [layerVisibility, geojsonData, map]);

  const handleVisibilityChange = useCallback((layerId, isVisible) => {
    setLayerVisibility(prev => ({ ...prev, [layerId]: isVisible }));
  }, []);

  const controllableLayersForControl = useMemo(() => {
    return Object.entries(layerConfigs).map(([key, config]) => ({ id: key, name: config.name }));
  }, []);

  return (
    <>
      <BaseTileLayer baseKey={baseLayer} />

      {Object.entries(layerConfigs).map(([key, config]) => {
        if (!layerVisibility[key]) return null;
        const data = geojsonData[key];
        if (config.type === 'raster') {
          if (!data || !Array.isArray(data.bounds)) return null;
          const bounds = [
            [data.bounds[1], data.bounds[0]],
            [data.bounds[3], data.bounds[2]],
          ];
          return (
            <ImageOverlay
              key={key}
              url={config.imageUrl}
              bounds={bounds}
              opacity={config.defaultOpacity ?? 0.85}
            />
          );
        }
        if (!data) return null;
        return (
          <GeoJSON
            key={key}
            data={data}
            style={config.style}
            onEachFeature={(feature, layer) => {
              const popupHtml = buildPopupHtml(key, feature);
              if (popupHtml) layer.bindPopup(popupHtml);
            }}
            pointToLayer={buildPointRenderer(config.pointStyle)}
          />
        );
      })}

      <LayerControl
        layers={controllableLayersForControl}
        layerVisibility={layerVisibility}
        onVisibilityChange={handleVisibilityChange}
        baseLayer={baseLayer}
        onBaseLayerChange={setBaseLayer}
        baseOptions={BASE_MAPS}
      />
    </>
  );
};

export default LayerManager;
